import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ArrowLeft from '@decisiv/iconix/lib/components/ArrowLeft';

import Button from '@decisiv/ui-components/lib/components/Button';
import Wizard from '@decisiv/ui-components/lib/components/Wizard';

import AccountStep from './AccountStep';
import CreatingPanel from './CreatingPanel';
import ErrorPanel from './ErrorPanel';
import PermissionsStep from './PermissionsStep';
import SuccessPanel from './SuccessPanel';
import UserDetailsStep from './UserDetailsStep';
import { CREATING_PANEL, STEPS, SUCCESS_PANEL, WIZARD_TITLE } from './copy';
import { WIZARD_Z_INDEX } from './layers';
import { toFieldMessages } from './serverErrors';
import { FooterActions } from './styles';
import useCreateUserForm, { toPayload } from '../../hooks/useCreateUserForm';
import useFormOptions from '../../hooks/useFormOptions';
import { createUser } from '../../services/createUserService';

export const PHASES = {
  form: 'form',
  creating: 'creating',
  success: 'success',
  error: 'error',
};

const toItems = (options) =>
  options.map(({ label, value }) => ({ id: String(value), label }));

/**
 * The Create User wizard (PD-4064).
 *
 * The KeyDS Wizard component is what the mockup draws — same centred title, same
 * StepTracker, same Cancel / Back / Continue footer — so the shell is the
 * library's and only the step bodies are ours.
 *
 * Four phases share the last step's slot. `form` is the normal flow; submitting
 * moves to `creating`, then to `success` or `error`. The tracker keeps showing
 * the last step throughout, as in the mockup, and `renderActions` replaces the
 * footer, because none of the three terminal phases wants Back and Continue.
 */
function CreateUserWizard({ onClose, onCreated, visible }) {
  const [activeStep, setActiveStep] = useState(1);
  const [phase, setPhase] = useState(PHASES.form);
  const [createdUser, setCreatedUser] = useState(null);

  // Whether anything was created since the wizard was last closed, which is a
  // different question from who the success panel names: CREATE NEW USER clears
  // `createdUser` to start a fresh form, and cancelling from there still leaves a
  // user the list has not fetched. Kept separate so that refetch cannot be lost.
  const [hasCreated, setHasCreated] = useState(false);

  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    options,
    searchAccounts,
  } = useFormOptions(visible);
  const {
    checks,
    checkEmail,
    checkMobile,
    draft,
    reset,
    serverMessages,
    setField,
    setServerMessages,
    stepValidity,
  } = useCreateUserForm();

  const validity = stepValidity();

  const restart = () => {
    reset();
    setCreatedUser(null);
    setPhase(PHASES.form);
    setActiveStep(1);
  };

  // However the wizard is left — CLOSE from the success panel, or CANCEL from a
  // form the admin opened with CREATE NEW USER and then abandoned — a user created
  // in it is only in the list once the page refetches, so tell it on the way out.
  // One call however many were created: the page bumps a counter, so a single
  // refetch brings all of them in.
  const close = () => {
    if (hasCreated) onCreated();

    setHasCreated(false);
    restart();
    onClose();
  };

  const submit = async () => {
    setPhase(PHASES.creating);
    setServerMessages({});

    try {
      setCreatedUser(await createUser(toPayload(draft)));
      setHasCreated(true);
      setPhase(PHASES.success);
    } catch (error) {
      // The server knows things the wizard cannot check — the per-country phone
      // rules, an email taken since the blur check, and every other rule on User.
      // When it says which field was wrong, show that on the field's own step
      // instead of the generic panel, which is what made a rejected phone number
      // look like a server fault.
      const { messages, step } = toFieldMessages(error.fieldErrors);

      if (step === null) {
        setPhase(PHASES.error);
        return;
      }

      setServerMessages(messages);
      setActiveStep(step);
      setPhase(PHASES.form);
    }
  };

  const terminalContent = () => {
    if (phase === PHASES.creating) return <CreatingPanel />;
    if (phase === PHASES.error) return <ErrorPanel onRetry={submit} />;

    return <SuccessPanel user={createdUser} onCreateAnother={restart} />;
  };

  const steps = [
    {
      title: STEPS.account,
      hideBodyTitle: true,
      nextButtonProps: {
        disabled: !validity[0],
        onClick: () => setActiveStep(2),
      },
      content: (
        <AccountStep
          hasMore={hasMoreAccounts}
          isLoading={isLoadingAccounts}
          options={accountOptions}
          selected={draft.accountIds}
          total={accountsTotal}
          onChange={(values) => setField('accountIds', values)}
          onLoadMore={loadMoreAccounts}
          onSearchAccounts={searchAccounts}
        />
      ),
    },
    {
      title: STEPS.details,
      hideBodyTitle: true,
      previousButtonProps: { onClick: () => setActiveStep(1) },
      nextButtonProps: {
        disabled: !validity[1],
        onClick: () => setActiveStep(3),
      },
      content: (
        <UserDetailsStep
          checks={checks}
          draft={draft}
          jobRoleOptions={toItems(options.jobRoles)}
          phoneCountryCodeOptions={toItems(options.phoneCountryCodes)}
          serverMessages={serverMessages}
          onBlurEmail={checkEmail}
          onBlurMobile={checkMobile}
          onChange={setField}
        />
      ),
    },
    {
      title: STEPS.permissions,
      hideBodyTitle: true,
      previousButtonProps: { onClick: () => setActiveStep(2) },
      content:
        phase === PHASES.form ? (
          <PermissionsStep
            accountCount={draft.accountIds.length}
            draft={draft}
            permissionOptions={options.permissions}
            onChange={setField}
          />
        ) : (
          terminalContent()
        ),
    },
  ];

  // The last step's own footer: CREATE USER, which the Wizard renders because it
  // is the submit button of the final step.
  const submitButtonProps = {
    disabled: !validity[2],
    text: 'Create User',
    onClick: submit,
  };

  return (
    <Wizard
      activeStep={activeStep}
      id="create-user-wizard"
      steps={steps}
      submitButtonProps={submitButtonProps}
      title={WIZARD_TITLE}
      visible={visible}
      zIndex={WIZARD_Z_INDEX}
      onCancel={close}
      renderActions={({ defaultActions, isLastStep }) => {
        if (!isLastStep || phase === PHASES.form) return defaultActions;

        // Creating: no way out but waiting, per the mockup's disabled action.
        if (phase === PHASES.creating) {
          return (
            <FooterActions>
              <Button disabled text={CREATING_PANEL.action} />
            </FooterActions>
          );
        }

        // Success closes; error offers a way back to the form as well. Both go
        // through FooterActions — see ./styles for why bare Buttons here end up
        // at opposite ends of the footer.
        return (
          <FooterActions>
            {phase === PHASES.error && (
              <Button
                kind="secondary"
                icon={ArrowLeft}
                text="Back"
                onClick={() => setPhase(PHASES.form)}
              />
            )}
            <Button text={SUCCESS_PANEL.close} onClick={close} />
          </FooterActions>
        );
      }}
    />
  );
}

CreateUserWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
  /** Called when a user was created, so the list can refetch. */
  onCreated: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

CreateUserWizard.defaultProps = {
  visible: false,
};

export default CreateUserWizard;
